<!-- Customers Table Component -->
<!-- Field names match old QMC API response (PascalCase) -->
<div class="customers-table">
  <p-table
    [value]="data?.data || []"
    [loading]="loading"
    [paginator]="true"
    [rows]="data?.pagination?.pageSize || 25"
    [totalRecords]="data?.pagination?.totalItems || 0"
    [lazy]="true"
    [sortMode]="'single'"
    [showCurrentPageReport]="true"
    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} customers"
    [rowsPerPageOptions]="pageSizeOptions"
    [scrollHeight]="scrollHeight"
    [scrollable]="true"
    scrollDirection="both"
    size="small"
    responsiveLayout="stack"
    breakpoint="768px"
    dataKey="Id"
    [rowExpandMode]="'single'"
    [expandedRowKeys]="expandedRows"
    (onRowExpand)="onRowExpand($event)"
    (onRowCollapse)="onRowCollapse($event)"
    (onSort)="onSort($event)"
    (onPage)="onPageChange($event)"
    styleClass="p-datatable-customers"
  >
    <!-- Table Header -->
    <ng-template pTemplate="header">
      <tr>
        <!-- Expand/Collapse Column (Frozen Left) -->
        <th
          style="width: 2.5rem"
          class="text-center"
          pFrozenColumn
          [frozen]="true"
          alignFrozen="left"
        ></th>
        <th
          pSortableColumn="Id"
          pFrozenColumn
          [frozen]="true"
          alignFrozen="left"
          style="width: 3rem"
        >
          ID
          <p-sortIcon field="Id" />
        </th>
        <th pSortableColumn="LastName" style="min-width: 150px">
          Name
          <p-sortIcon field="LastName" />
        </th>
        <th pSortableColumn="Email" style="min-width: 200px">
          Email
          <p-sortIcon field="Email" />
        </th>
        <th style="width: 100px; text-align: center">Active / total</th>
        <th style="width: 100px; text-align: center">Paused</th>
        <th style="width: 120px; text-align: right">LTV</th>
        <th
          pSortableColumn="NextScheduledOrderOccurrenceUtc"
          style="width: 120px"
        >
          Next order
          <p-sortIcon field="NextScheduledOrderOccurrenceUtc" />
        </th>
        <th pSortableColumn="CreatedUtc" style="width: 120px">
          Created
          <p-sortIcon field="CreatedUtc" />
        </th>
        <th
          pFrozenColumn
          [frozen]="true"
          alignFrozen="right"
          style="width: 3rem; text-align: center"
        >
          Actions
        </th>
      </tr>
    </ng-template>

    <!-- Table Body -->
    <ng-template pTemplate="body" let-customer let-expanded="expanded">
      <tr
        class="customer-row"
        [class.clickable-row]="true"
        [class.deleted-row]="!customer.Active"
        (click)="toggleRowExpansion(customer)"
      >
        <!-- Row Expansion Toggle (Frozen Left) -->
        <td
          class="text-center expand-column"
          pFrozenColumn
          [frozen]="true"
          alignFrozen="left"
          (click)="$event.stopPropagation()"
        >
          <p-button
            [pRowToggler]="customer"
            type="button"
            pRipple
            [text]="true"
            [rounded]="true"
            [plain]="true"
            size="small"
            class="text-gray-400 hover:text-gray-600"
            [icon]="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
            [disabled]="loading"
            pTooltip="Click to expand/collapse details"
            tooltipPosition="top"
          />
        </td>

        <!-- ID Column (Frozen Left) - Click opens edit, stops row expansion -->
        <td class="id-column" pFrozenColumn [frozen]="true" alignFrozen="left">
          <a
            href="javascript:;"
            class="id-link"
            (click)="onRowClick(customer); $event.stopPropagation()"
          >
            {{ customer.Id }}
          </a>
        </td>

        <!-- Name Column - Click searches scheduled orders by last name (desktop only) -->
        <td class="name-column">
          <span class="p-column-title">Name</span>
          <div class="customer-name-cell">
            <!-- Desktop: Clickable link to search scheduled orders -->
            <a
              href="javascript:;"
              class="customer-name-link desktop-only"
              (click)="
                searchScheduledOrders(
                  customer,
                  customer.LastName || customer.FirstName || ''
                );
                $event.stopPropagation()
              "
              pTooltip="Search scheduled orders by last name"
              tooltipPosition="top"
              >{{ getCustomerFullName(customer) }}</a
            >
            <!-- Mobile: Plain text name (no link) -->
            <span class="customer-name mobile-only">{{
              getCustomerFullName(customer)
            }}</span>
            @if (!customer.Active) {
              <p-tag value="Deleted" severity="danger" class="ml-2" />
            }
          </div>
        </td>

        <!-- Email Column - Click searches scheduled orders by email -->
        <td class="email-column">
          <span class="p-column-title">Email</span>
          <a
            href="javascript:;"
            class="customer-email-link"
            (click)="
              searchScheduledOrders(customer, customer.Email || '');
              $event.stopPropagation()
            "
            pTooltip="Search scheduled orders by email"
            tooltipPosition="top"
          >
            {{ customer.Email || "-" }}
          </a>
        </td>

        <!-- Active / Total Scheduled Orders Column -->
        <td class="so-column text-center">
          <span class="p-column-title">Active/Total</span>
          @if ((customer.TotalScheduledOrdersCount || 0) > 0) {
            <p-tag
              [value]="
                (customer.ActiveScheduledOrdersCount || 0) +
                ' / ' +
                (customer.TotalScheduledOrdersCount || 0)
              "
              severity="success"
              [pTooltip]="
                (customer.ActiveScheduledOrdersCount || 0) +
                ' active of ' +
                (customer.TotalScheduledOrdersCount || 0) +
                ' total'
              "
              tooltipPosition="top"
            />
          } @else {
            <span class="text-gray-400">-</span>
          }
        </td>

        <!-- Paused Column (hidden on mobile - shown in expanded row) -->
        <td class="paused-column text-center desktop-only-cell">
          <span class="p-column-title">Paused</span>
          @if ((customer.PausedScheduledOrdersCount || 0) > 0) {
            <p-tag
              [value]="(customer.PausedScheduledOrdersCount || 0).toString()"
              severity="warn"
              pTooltip="Paused scheduled orders"
              tooltipPosition="top"
            />
          } @else {
            <span class="text-gray-400">-</span>
          }
        </td>

        <!-- LTV Column (hidden on mobile - shown in expanded row) -->
        <td class="ltv-column text-right desktop-only-cell">
          <span class="p-column-title">LTV</span>
          @if (customer.ScheduledOrderLTV) {
            <span class="ltv-value">{{
              customer.ScheduledOrderLTV | currency
            }}</span>
          } @else {
            <span class="text-gray-400">-</span>
          }
        </td>

        <!-- Next Order Column -->
        <td class="next-order-column">
          <span class="p-column-title">Next Order</span>
          <span class="next-order-date">{{
            formatDate(customer.NextScheduledOrderOccurrenceUtc)
          }}</span>
        </td>

        <!-- Created Column -->
        <td class="created-column">
          <span class="p-column-title">Created</span>
          <span class="created-date">{{
            formatDate(customer.CreatedUtc)
          }}</span>
        </td>

        <!-- Actions Column (Frozen Right) -->
        <td
          class="actions-column text-center"
          pFrozenColumn
          [frozen]="true"
          alignFrozen="right"
          (click)="$event.stopPropagation()"
        >
          <button
            pButton
            type="button"
            icon="pi pi-ellipsis-v"
            class="p-button-rounded p-button-text p-button-sm"
            (click)="toggleRowMenu($event, customer)"
            pTooltip="Actions"
            tooltipPosition="left"
          ></button>
        </td>
      </tr>
    </ng-template>

    <!-- Row Expansion Template -->
    <ng-template pTemplate="expandedrow" let-customer>
      <tr class="expanded-row">
        <td colspan="10" class="expanded-row-cell">
          <div class="expanded-row-content">
            <!-- Quick Actions -->
            <div class="quick-actions-section">
              <p-button
                label="Scheduled Orders"
                icon="pi pi-calendar"
                size="small"
                severity="secondary"
                [outlined]="true"
                (click)="
                  rowAction.emit({ action: 'viewScheduledOrders', customer })
                "
                [disabled]="(customer.TotalScheduledOrdersCount || 0) === 0"
              />
              <p-button
                label="Edit"
                icon="pi pi-pencil"
                size="small"
                severity="secondary"
                [outlined]="true"
                (click)="rowAction.emit({ action: 'edit', customer })"
              />
            </div>

            <!-- Mobile-only: Paused and LTV info (hidden on desktop) -->
            <div class="detail-section mobile-only-section">
              <div class="section-header">Subscription Status</div>
              <dl class="detail-list">
                <div class="detail-item">
                  <dt>Paused Orders</dt>
                  <dd>
                    @if ((customer.PausedScheduledOrdersCount || 0) > 0) {
                      <p-tag
                        [value]="
                          (customer.PausedScheduledOrdersCount || 0).toString()
                        "
                        severity="warn"
                      />
                    } @else {
                      <span>None</span>
                    }
                  </dd>
                </div>
                <div class="detail-item">
                  <dt>Lifetime Value</dt>
                  <dd>
                    @if (customer.ScheduledOrderLTV) {
                      {{ customer.ScheduledOrderLTV | currency }}
                    } @else {
                      <span>-</span>
                    }
                  </dd>
                </div>
              </dl>
            </div>

            <!-- Detail Sections - flat grid layout (matches scheduled-orders) -->
            <!-- Payment Information -->
            <div class="detail-section">
              <div class="section-header">Payment Information</div>
              <dl class="detail-list">
                <div class="detail-item">
                  <dt>Default Method</dt>
                  <dd>{{ customer.DefaultPaymentMethod || "None" }}</dd>
                </div>
                <div class="detail-item">
                  <dt>Saved Methods</dt>
                  <dd>{{ customer.PaymentMethodsCount || 0 }}</dd>
                </div>
              </dl>
            </div>

            <!-- Orders Summary -->
            <div class="detail-section">
              <div class="section-header">Orders Summary</div>
              <dl class="detail-list">
                <div class="detail-item">
                  <dt>Total Products</dt>
                  <dd>{{ customer.TotalProductsScheduledCount || 0 }}</dd>
                </div>
                <div class="detail-item">
                  <dt>Total Value</dt>
                  <dd>
                    {{ customer.TotalScheduledOrderValue || 0 | currency }}
                  </dd>
                </div>
                <div class="detail-item">
                  <dt>Avg Order</dt>
                  <dd>
                    {{ customer.AverageScheduledOrderTotal || 0 | currency }}
                  </dd>
                </div>
              </dl>
            </div>

            <!-- Timeline -->
            <div class="detail-section">
              <div class="section-header">Timeline</div>
              <dl class="detail-list">
                <div class="detail-item">
                  <dt>Created</dt>
                  <dd>{{ formatDate(customer.CreatedUtc) }}</dd>
                </div>
                <div class="detail-item">
                  <dt>Updated</dt>
                  <dd>{{ formatDate(customer.UpdatedUtc) }}</dd>
                </div>
                @if (customer.LastScheduledOrderOccurrenceUtc) {
                  <div class="detail-item">
                    <dt>Last Processed</dt>
                    <dd>
                      {{ formatDate(customer.LastScheduledOrderOccurrenceUtc) }}
                    </dd>
                  </div>
                }
                @if (customer.NextScheduledOrderOccurrenceUtc) {
                  <div class="detail-item">
                    <dt>Next Order</dt>
                    <dd>
                      {{ formatDate(customer.NextScheduledOrderOccurrenceUtc) }}
                    </dd>
                  </div>
                }
              </dl>
            </div>
          </div>
        </td>
      </tr>
    </ng-template>

    <!-- Empty State -->
    <ng-template pTemplate="emptymessage">
      <tr>
        <td colspan="10" class="text-center py-8">
          <div class="empty-state">
            <i class="pi pi-users text-4xl text-gray-300 mb-3"></i>
            <p class="text-gray-500 mb-0">No customers found</p>
            <p class="text-gray-400 text-sm">
              Try adjusting your filters<br />or search criteria
            </p>
          </div>
        </td>
      </tr>
    </ng-template>

    <!-- Loading State -->
    <ng-template pTemplate="loadingbody">
      <app-table-skeleton-rows [rows]="5" [columns]="skeletonColumns" />
    </ng-template>
  </p-table>

  <!-- Context Menu -->
  <p-menu
    #contextMenu
    [popup]="true"
    [model]="contextMenuItems"
    appendTo="body"
  />
</div>
